import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowForward, PersonOutline } from '@mui/icons-material';
import { Skeleton, Tooltip } from '@mui/material';
import AssigneeAvatarStack from '../../../shared/AssigneeAvatarStack';
import { getAssigneeColor, getTaskTypeColor, getPriorityColor } from '../../dashboard/calendar-view/calendarColorUtils';
import { BACKLOG_CARD_TYPES, SPRINT_STATUSES } from '../../../shared/appConstants';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../../../shared/colors';

const BacklogSidebar = ({
  currentUserInitials,
  myAssignedBacklogCount = 0,
  totalBacklogCount = 0,
  isExpandedView,
  selectedCardType,
  sprints,
  tickets,
  dragOverCardId,
  setDragOverCardId,
  handleDropOnTarget,
  formatDateRange,
  currentMainView,
  setCurrentMainView,
  currentMainSprintId,
  getFilteredTicketsForScope,
  isAuthorized,
  handleDragStart,
  isLoading
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleAvatarClick = (assignee) => {
    let name = '';
    if (typeof assignee === 'string') {
      name = assignee;
    } else if (assignee) {
      name = assignee.name || `${assignee.first_name || ''} ${assignee.last_name || ''}`.trim();
    }
    
    if (name) {
      navigate(`/user-management?search=${encodeURIComponent(name)}`);
    } else {
      navigate(`/user-management`);
    }
  };

  if (isLoading) {
    return (
      <div className={`backlog-sidebar ${isExpandedView ? 'collapsed' : ''}`}>
        <h3 className="sidebar-title">
          {t('backlog.sprintPlan')}
        </h3>
        
        <div className="sidebar-panel-card" style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Skeleton variant="text" width="40%" height={28} />
            <Skeleton variant="text" width="30%" height={20} />
          </div>
          <Skeleton variant="rectangular" width="100%" height={48} style={{ borderRadius: '6px' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Skeleton variant="text" width="100%" height={16} />
            <Skeleton variant="text" width="80%" height={16} />
          </div>
          <Skeleton variant="rectangular" width="100%" height={32} style={{ borderRadius: '4px', marginTop: '8px' }} />
        </div>
        
        <div className="sidebar-section-title" style={{ marginTop: '16px' }}>
          <Skeleton variant="text" width="30%" height={24} />
        </div>
        
        <div className="sidebar-panel-card sidebar-panel-card-inner" style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px' }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < 4 ? '1px solid #f1f5f9' : 'none' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                <Skeleton variant="text" width="20%" height={16} />
                <Skeleton variant="text" width="70%" height={20} />
              </div>
              <Skeleton variant="circular" width={24} height={24} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`backlog-sidebar ${isExpandedView ? 'collapsed' : ''}`}>
      <h3 className="sidebar-title" >
        {t('backlog.sprintPlan')}
      </h3>

      {/* 1. Active Sprint Card */}
      {(selectedCardType === BACKLOG_CARD_TYPES.ACTIVE_SPRINT || selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS || selectedCardType === 'completion-rate') && (
        sprints.filter(s => s.status === SPRINT_STATUSES.ACTIVE).length > 0 ? (
          sprints.filter(s => s.status === SPRINT_STATUSES.ACTIVE).map((sprint) => {
            let sprintTickets = tickets.filter(t => t.sprintId === sprint.id);
            if (selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS) {
              sprintTickets = sprintTickets.filter(t => t.assignee === currentUserInitials || (t.assignees && t.assignees.includes(currentUserInitials)));
            }
            const sprintPointsTotal = sprint.totalPoints !== undefined ? sprint.totalPoints : sprintTickets.reduce((sum, t) => sum + t.sp, 0);
            const sprintPointsCompleted = sprint.completedStory !== undefined ? sprint.completedStory : sprintTickets.filter(t => t.status === 'Done').reduce((sum, t) => sum + t.sp, 0);

            const sprintProgress = sprint.completedPercentage !== undefined ? sprint.completedPercentage : (sprintPointsTotal > 0 ? Math.round((sprintPointsCompleted / sprintPointsTotal) * 100) : 0);
            const p1Pending = sprint.pendingP1Count !== undefined ? sprint.pendingP1Count : sprintTickets.filter(t => t.status !== 'Done' && ((typeof t.priority === 'object' ? t.priority.name : t.priority) || '').toLowerCase() === 'critical').length;

            return (
              <div 
                key={sprint.id}
                className={`sidebar-panel-card drag-target-zone ${dragOverCardId === sprint.id ? 'drag-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOverCardId(sprint.id); }}
                onDragLeave={() => setDragOverCardId(null)}
                onDrop={(e) => { handleDropOnTarget(e, sprint.id); setDragOverCardId(null); }}
              >
                <div className="sidebar-panel-header" style={{ alignItems: 'flex-start' }}>
                  <div className="sidebar-panel-header-info" style={{ flex: 1, minWidth: 0, marginRight: '12px' }}>
                    <h4 className="sidebar-panel-title" style={{ display: 'flex', alignItems: 'center', margin: 0, minWidth: 0, gap: '6px' }}>
                      <Tooltip title={sprint.name?.length > 20 ? sprint.name : ''} arrow placement="top">
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1, display: 'block' }}>
                          {sprint.name}
                        </span>
                      </Tooltip>
                      <span className="sidebar-badge active" style={{ display: 'inline-block', flexShrink: 0 }}>{t('backlog.active')}</span>
                    </h4>
                  </div>
                  <span className="sidebar-panel-dates sidebar-panel-dates-container" style={{ flexShrink: 0, marginTop: '2px' }}>
                    {formatDateRange(sprint.startDate, sprint.endDate)}
                  </span>
                </div>

                <div className="sidebar-stats-row">
                  <div className="sidebar-stat-item">
                    <span className="sidebar-stat-val">{sprintProgress}%</span>
                    <span className="sidebar-stat-lbl">
                      <span style={{ fontWeight: 600 }}>{sprintPointsCompleted}</span> of <span style={{ fontWeight: 600 }}>{sprintPointsTotal}</span> SP
                    </span>
                  </div>
                  <div className="sidebar-stat-item">
                    <span className="sidebar-stat-val">{p1Pending}</span>
                    <span className="sidebar-stat-lbl">{t('backlog.p1Pending')}</span>
                  </div>
                  <div className="sidebar-stat-item">
                    <span className="sidebar-stat-val">{sprint.remainingDays !== undefined ? sprint.remainingDays : 5} days</span>
                    <span className="sidebar-stat-lbl">{t('backlog.remaining')}</span>
                  </div>
                </div>

                <div className="sidebar-goal-section" >
                  <span className="sidebar-goal-title">{t('backlog.goal')}</span>
                  <p className="sidebar-goal">{sprint.goal || 'No goal set'}</p>
                </div>

                <div className="sidebar-progress-section" >
                  <div className="sidebar-progress-bar-bg">
                    <div className="sidebar-progress-bar-fill" style={{ width: `${sprintProgress}%` }}></div>
                  </div>
                </div>

                {currentMainSprintId !== sprint.id && currentMainView !== 'my_tickets' && (
                  <button 
                    className="sidebar-view-btn"
                    onClick={() => setCurrentMainView(sprint.id)}
                  >
                    {t('backlog.viewSprint')} <ArrowForward sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
                  </button>
                )}
              </div>
            );
          })
        ) : (
          <div className="sidebar-panel-card" style={{ padding: '32px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', borderStyle: 'dashed' }}>
            <span style={{ color: COLORS.slate[600], fontWeight: 600 }}>No Active Sprint</span>
            <span style={{ fontSize: '0.85rem', color: COLORS.slate[500] }}>There is currently no active sprint in progress. You can start a new sprint from the planned sprints list.</span>
          </div>
        )
      )}

      {/* 2. Backlog Preview Card list */}
      {currentMainView !== 'backlog' && (() => {
        let backlogList = getFilteredTicketsForScope('backlog');
        const count = selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS ? myAssignedBacklogCount : backlogList.filter(t => t.assignee === currentUserInitials || (t.assignees && t.assignees.includes(currentUserInitials))).length;

        return (
        <>
          <div className="sidebar-section-title">
            <span>{t('backlog.title').split(' ')[0]} {selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS ? `(${count} ${t('backlog.assignedToYou')})` : ''}</span>
          </div>

          <div 
            className={`sidebar-panel-card sidebar-panel-card-inner drag-target-zone ${dragOverCardId === 'backlog' ? 'drag-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOverCardId('backlog'); }}
            onDragLeave={() => setDragOverCardId(null)}
            onDrop={(e) => { handleDropOnTarget(e, 'backlog'); setDragOverCardId(null); }}
          >
            <div className="sidebar-backlog-list" style={{ maxHeight: '200px', overflowY: 'auto', margin: '0 -4px 0 -12px', padding: '0 4px 0 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {backlogList.length === 0 ? (
                <div className="table-empty-state" style={{ padding: '20px', fontSize: '0.8rem' }}>
                  {t('backlog.noBacklogTickets')}
                </div>
              ) : (
                <>
                  {backlogList.slice(0, 5).map(t => (
                    <div 
                      className="sidebar-backlog-item draggable-row" 
                      key={t.id}
                      draggable={isAuthorized}
                      onDragStart={(e) => handleDragStart(e, t)}
                    >
                      <div className="sidebar-backlog-item-details">
                        <span className="sidebar-backlog-item-code">{t.code}</span>
                        <p className="sidebar-backlog-item-title">{t.title}</p>
                        <div className="sidebar-backlog-item-meta">
                          <span 
                            className="badge-pill" 
                            style={{ 
                              fontSize: '0.65rem', 
                              padding: '1px 6px',
                              backgroundColor: `${getTaskTypeColor(t.type)}15`,
                              color: getTaskTypeColor(t.type),
                              border: `1px solid ${getTaskTypeColor(t.type)}30`
                            }}
                          >
                            {t.type}
                          </span>
                          <span 
                            className="badge-pill" 
                            style={{ 
                              fontSize: '0.65rem', 
                              padding: '1px 6px',
                              backgroundColor: `${getPriorityColor(t.priority)}15`,
                              color: getPriorityColor(t.priority),
                              border: `1px solid ${getPriorityColor(t.priority)}30`
                            }}
                          >
                            {typeof t.priority === 'object' ? t.priority.name : (t.priority || '')}
                          </span>
                        </div>
                      </div>
                      
                      <div className="sidebar-backlog-item-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', flexShrink: 0 }}>
                        { (t.assignees && t.assignees.length > 0) || (Array.isArray(t.assignee) ? t.assignee.length > 0 : !!t.assignee) ? (
                          <AssigneeAvatarStack assignees={t.assignees} assignee={t.assignee} size={20} colorResolver={getAssigneeColor} onAvatarClick={handleAvatarClick} />
                        ) : (
                          <div className="backlog-assignee-avatar" style={{ width: '20px', height: '20px' }}>
                            <PersonOutline sx={{ fontSize: 14 }} />
                          </div>
                        )}
                        <span style={{ fontSize: '0.7rem', color: COLORS.slate[500], fontWeight: 600 }}>{t.sp} SP</span>
                      </div>
                    </div>
                  ))}
                  {totalBacklogCount > 5 && (
                    <div style={{ textAlign: 'center', fontSize: '0.75rem', color: COLORS.slate[500], padding: '8px 0', fontWeight: 500 }}>
                      +{totalBacklogCount - 5} {t('backlog.moreItemsInBacklog')}
                    </div>
                  )}
                </>
              )}
            </div>

            {currentMainView !== 'backlog' && (
              <button 
                className="sidebar-view-btn"
                onClick={() => setCurrentMainView('backlog')}
                style={{ width: '100%', marginTop: '8px' }}
              >
                {t('backlog.viewBacklog')} <ArrowForward sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
              </button>
            )}
          </div>
        </>
        );
      })()}

      {/* 3. Planned/{t('backlog.upcomingSprints')} */}
      {selectedCardType === BACKLOG_CARD_TYPES.PLANNED_SPRINT && sprints.filter(s => s.status === SPRINT_STATUSES.PLANNED).length > 0 && (
        <div className="sidebar-section-title">
          <span>{t('backlog.upcomingSprints')}</span>
        </div>
      )}
      {selectedCardType === BACKLOG_CARD_TYPES.PLANNED_SPRINT && sprints.filter(s => s.status === SPRINT_STATUSES.PLANNED).map((sprint, idx) => {
        const sprintTickets = tickets.filter(t => t.sprintId === sprint.id);
        const sprintPointsTotal = sprintTickets.reduce((sum, t) => sum + t.sp, 0);
        const sprintPointsCompleted = sprintTickets.filter(t => t.status === 'Done').reduce((sum, t) => sum + t.sp, 0);
        const p1Pending = sprintTickets.filter(t => t.status !== 'Done' && ((typeof t.priority === 'object' ? t.priority.name : t.priority) || '').toLowerCase() === 'critical').length;


        return (
          <div key={sprint.id}>
            <div 
              className={`sidebar-panel-card drag-target-zone ${dragOverCardId === sprint.id ? 'drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOverCardId(sprint.id); }}
              onDragLeave={() => setDragOverCardId(null)}
              onDrop={(e) => { handleDropOnTarget(e, sprint.id); setDragOverCardId(null); }}
            >
              <div className="sidebar-panel-header" style={{ alignItems: 'center' }}>
                <div className="sidebar-panel-header-info" style={{ flex: 1, minWidth: 0, marginRight: '12px' }}>
                  <h4 className="sidebar-panel-title" style={{ display: 'flex', alignItems: 'center', margin: 0, minWidth: 0, gap: '6px' }}>
                    <Tooltip title={sprint.name?.length > 20 ? sprint.name : ''} arrow placement="top">
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1, display: 'block' }}>
                        {sprint.name}
                      </span>
                    </Tooltip>
                    <span className="sidebar-badge upcoming" style={{ display: 'inline-block', flexShrink: 0 }}>{t('backlog.upcoming')}</span>
                  </h4>
                </div>
                <span className="sidebar-panel-dates" style={{ flexShrink: 0, marginTop: '2px' }}>
                  {formatDateRange(sprint.startDate, sprint.endDate)}
                </span>
              </div>

              <div className="sidebar-stats-row-upcoming" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', padding: '10px 0', margin: '4px 0' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: COLORS.slate[700] }}>
                  {sprintPointsCompleted} of {sprintPointsTotal} SP
                </span>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: COLORS.slate[500] }}>
                  <span style={{ color: COLORS.red[500], marginRight: '4px', fontWeight: 700 }}>{p1Pending}</span>{t('backlog.p1Pending')}</span>
              </div>

              <div className="sidebar-goal-section" >
                <span className="sidebar-goal-title">{t('backlog.goal')}</span>
                <p className="sidebar-goal">{sprint.goal}</p>
              </div>

              {(currentMainView === 'backlog' || currentMainSprintId !== sprint.id) && (
                <button 
                  className="sidebar-view-btn"
                  onClick={() => setCurrentMainView(sprint.id)}
                >
                  {t('backlog.viewSprint')} <ArrowForward sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BacklogSidebar;
